import { useMutation } from '@tanstack/react-query'
import { Button, EmptyState, Modal, Text, UnsavedChangesDialog, useUnsavedChangesWarning } from '@viapeople/components'
import LexicalMarkdownEditor, { MarkdownPreview } from '@viapeople/components/src/components/MarkdownEditor'
import { useEffect, useState } from 'react'
import { useToast } from '../../../../utils/ToastContext'
import { queryClient, trpc } from '../../../../utils/trpc'
import type { Cycle } from '../../types'
import { useCycleFromParams } from '../../useCycleFromParams'

const InstructionsModal = ({
	isOpen,
	onClose,
	isEditing,
	cycle,
}: {
	isOpen: boolean
	onClose: () => void
	isEditing: boolean
	cycle?: Cycle
}) => {
	const toast = useToast()
	const { mutate: editCycle, isPending } = useMutation(
		trpc.admin.cycles.edit.mutationOptions({
			onSuccess: () => {
				queryClient.setQueryData(
					trpc.admin.cycles.getById.queryKey({
						id: cycle?.id,
					}),
					() => ({
						...(cycle as Cycle),
						reviewerInstructions: _value || null,
					}),
				)
				toast.showToast('Instructions updated successfully', 'info')
				onClose()
			},
			onError: () => {
				toast.showToast('Error updating instructions', 'error')
			},
			onSettled: () => {
				queryClient.invalidateQueries({
					queryKey: trpc.admin.cycles.getById.queryKey({ id: cycle?.id }),
					exact: false,
					refetchType: 'all',
				})
				queryClient.invalidateQueries({
					queryKey: trpc.admin.cycles.search.queryKey(),
					exact: false,
					refetchType: 'all',
				})
			},
		}),
	)

	const [_value, setValue] = useState(cycle?.reviewerInstructions || '')
	const isDirty = _value !== (cycle?.reviewerInstructions || '')
	const { isBlocked, confirmLeave, cancelLeave } = useUnsavedChangesWarning(isDirty && isOpen)

	useEffect(() => {
		setValue(cycle?.reviewerInstructions || '')
	}, [cycle?.reviewerInstructions])

	const handleChange = (value: string) => {
		setValue(value)
	}

	const onSave = (instructions: string) => {
		if (cycle?.id) {
			editCycle({
				id: cycle.id,
				organizationId: cycle.organizationId,
				reviewerInstructions: instructions,
				metadata: null,
				triggerLogId: null,
			})
		}
	}

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title={`${isEditing ? 'Edit' : 'Add'} Instructions`}
			className='min-w-[800px]'>
			<LexicalMarkdownEditor onChangeMarkdown={handleChange} defaultValue={cycle?.reviewerInstructions || ''} />

			<div className='flex flex-row justify-between'>
				{isEditing ? (
					<Button
						variant='destructive'
						isLoading={isPending}
						disabled={isPending}
						onClick={() => {
							setValue('')
							onSave('')
						}}>
						Delete
					</Button>
				) : (
					<div />
				)}
				<div className='flex flex-row gap-4 mt-auto self-end'>
					<Button variant='secondary' isLoading={isPending} disabled={isPending} onClick={onClose}>
						Cancel
					</Button>
					<Button onClick={() => onSave(_value || '')} isLoading={isPending} disabled={isPending}>
						Save
					</Button>
				</div>
			</div>
			<UnsavedChangesDialog isBlocked={isBlocked} confirmLeave={confirmLeave} cancelLeave={cancelLeave} />
		</Modal>
	)
}

export function CycleReviewersInstructionsTab() {
	const cycle = useCycleFromParams()
	const [openModal, setOpenModal] = useState(false)

	return (
		<div>
			<div className='mt-6'>
				<Text variant='h6' weight='semibold' className='text-left'>
					Instructions
				</Text>

				{!cycle?.reviewerInstructions && (
					<EmptyState
						title='Instructions'
						description='Click below to add instructions for reviewers'
						actionLabel='Add Instruction'
						onAction={() => {
							setOpenModal(!!cycle?.id)
						}}
					/>
				)}

				{cycle?.reviewerInstructions && (
					<div className='mt-4'>
						{/* <Text className="text-left my-4 text-neutral-500">{cycle.reviewerInstructions}</Text> */}
						<MarkdownPreview markdown={cycle.reviewerInstructions} />

						<Button variant='primary' onClick={() => setOpenModal(true)} className='ml-auto'>
							Edit Instructions
						</Button>
					</div>
				)}

				<InstructionsModal
					isOpen={openModal}
					onClose={() => setOpenModal(false)}
					isEditing={!!cycle?.reviewerInstructions}
					cycle={cycle}
				/>
			</div>
		</div>
	)
}
